import React from "react";
import { motion } from "framer-motion";
import CountUp from "react-countup";
import { Zap, Briefcase, Factory, Building2, Handshake } from "lucide-react";
import PageHero from "@/components/shared/PageHero";
import { useLang } from "@/i18n/LanguageContext";

const ODD_ICONS = [Zap, Briefcase, Factory, Building2, Handshake];

const HERO_IMG =
  "https://images.pexels.com/photos/11219000/pexels-photo-11219000.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=800&w=1600";

export default function Impact() {
  const { t } = useLang();

  return (
    <div data-testid="impact-page">
      <PageHero
        overline={t.impact.hero.overline}
        title={t.impact.hero.title}
        titleItalic={t.impact.hero.titleItalic}
        subtitle={t.impact.hero.subtitle}
        image={HERO_IMG}
      />

      {/* Impact stats */}
      <section className="bg-ekip-cream py-20 md:py-32" data-testid="impact-stats">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10 lg:px-14">
          <div className="grid grid-cols-12 gap-6 md:gap-10 mb-16">
            <div className="col-span-12 md:col-span-4 flex items-start gap-3">
              <span className="diamond mt-2" />
              <span className="text-[11px] uppercase tracking-[0.35em] text-ekip-copper font-medium">
                {t.impact.stats.eyebrow}
              </span>
            </div>
            <h2 className="col-span-12 md:col-span-8 font-display font-semibold text-3xl md:text-5xl leading-[1.05] text-ekip-navy">
              {t.impact.stats.title}
              <br />
              <em className="italic font-light text-ekip-copper">{t.impact.stats.titleItalic}</em>
            </h2>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-y-14 gap-x-6 border-t border-ekip-gold/20 pt-12">
            {t.impact.stats.items.map((it, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.6, delay: i * 0.06 }}
              >
                <div className="flex items-baseline gap-1">
                  <span className="font-display font-bold text-4xl md:text-6xl text-ekip-copper leading-none">
                    <CountUp end={it.value} duration={2.4} enableScrollSpy scrollSpyOnce separator=" " />
                  </span>
                  <span className="font-display font-bold text-2xl text-ekip-copper">{it.suffix}</span>
                </div>
                <div className="mt-5 h-px w-10 bg-ekip-copper" />
                <p className="mt-3 text-xs uppercase tracking-[0.22em] text-ekip-graphite font-body font-medium">
                  {it.label}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ODD */}
      <section className="bg-white py-20 md:py-32 border-y border-ekip-gold/20" data-testid="impact-odd">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10 lg:px-14">
          <div className="grid grid-cols-12 gap-6 md:gap-10 mb-16">
            <div className="col-span-12 md:col-span-4 flex items-start gap-3">
              <span className="diamond mt-2" />
              <span className="text-[11px] uppercase tracking-[0.35em] text-ekip-copper font-medium">
                {t.impact.odd.eyebrow}
              </span>
            </div>
            <h2 className="col-span-12 md:col-span-8 font-display font-semibold text-3xl md:text-5xl leading-[1.05] text-ekip-navy">
              {t.impact.odd.title}
              <br />
              <em className="italic font-light text-ekip-copper">{t.impact.odd.titleItalic}</em>
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {t.impact.odd.items.map((it, i) => {
              const Icon = ODD_ICONS[i];
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.6, delay: i * 0.06 }}
                  className="bg-ekip-cream border border-ekip-gold/20 p-6 md:p-8 hover:border-ekip-copper transition-colors"
                >
                  <div className="w-11 h-11 flex items-center justify-center border border-ekip-copper/50" style={{ transform: "rotate(45deg)" }}>
                    <Icon size={18} strokeWidth={1.4} className="text-ekip-copper" style={{ transform: "rotate(-45deg)" }} />
                  </div>
                  <span className="mt-6 block text-[10px] uppercase tracking-[0.3em] text-ekip-copper font-medium">
                    {it.code}
                  </span>
                  <h3 className="mt-2 font-display font-semibold text-base md:text-lg text-ekip-navy leading-tight">
                    {it.title}
                  </h3>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Innovation */}
      <section className="bg-ekip-navy py-20 md:py-32" data-testid="impact-innovation">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10 lg:px-14">
          <div className="grid grid-cols-12 gap-6 md:gap-10 mb-14">
            <div className="col-span-12 md:col-span-4 flex items-start gap-3">
              <span className="diamond mt-2" />
              <span className="text-[11px] uppercase tracking-[0.35em] text-ekip-copper font-medium">
                {t.impact.innovation.eyebrow}
              </span>
            </div>
            <h2 className="col-span-12 md:col-span-8 font-display font-semibold text-3xl md:text-5xl leading-[1.05] text-white">
              {t.impact.innovation.title}
              <br />
              <em className="italic font-light text-ekip-copper">{t.impact.innovation.titleItalic}</em>
            </h2>
          </div>

          <p className="max-w-3xl text-white/80 font-body font-light leading-relaxed text-lg mb-14">
            {t.impact.innovation.text}
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 border-t border-ekip-gold/20 pt-14">
            {t.impact.innovation.stats.map((s, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.08 }}
              >
                <div className="flex items-baseline gap-1">
                  <span className="font-display font-bold text-5xl md:text-6xl text-ekip-copper leading-none">
                    <CountUp end={s.value} duration={2.4} enableScrollSpy scrollSpyOnce />
                  </span>
                  <span className="font-display font-bold text-2xl text-ekip-copper">{s.suffix}</span>
                </div>
                <div className="mt-5 h-px w-10 bg-ekip-copper" />
                <p className="mt-3 text-xs uppercase tracking-[0.22em] text-white/75 font-body font-medium">
                  {s.label}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Territorial */}
      <section className="bg-ekip-cream py-20 md:py-32" data-testid="impact-territorial">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10 lg:px-14">
          <div className="grid grid-cols-12 gap-6 md:gap-10 mb-14">
            <div className="col-span-12 md:col-span-4 flex items-start gap-3">
              <span className="diamond mt-2" />
              <span className="text-[11px] uppercase tracking-[0.35em] text-ekip-copper font-medium">
                {t.impact.territorial.eyebrow}
              </span>
            </div>
            <h2 className="col-span-12 md:col-span-8 font-display font-semibold text-3xl md:text-5xl leading-[1.05] text-ekip-navy">
              {t.impact.territorial.title}
              <br />
              <em className="italic font-light text-ekip-copper">{t.impact.territorial.titleItalic}</em>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {t.impact.territorial.items.map((it, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.6, delay: i * 0.08 }}
                className="bg-white border border-ekip-gold/20 p-8 md:p-10 hover:border-ekip-copper transition-colors"
              >
                <span className="text-[10px] uppercase tracking-[0.3em] text-ekip-copper font-medium">
                  0{i + 1}
                </span>
                <h3 className="mt-4 font-display font-semibold text-xl md:text-2xl text-ekip-navy">
                  {it.title}
                </h3>
                <p className="mt-4 text-ekip-graphite font-body font-light leading-relaxed">
                  {it.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
